import type { ReactNode } from 'react'
import { SiteHeader } from './SiteHeader'
import { VerifyingBanner } from './VerifyingBanner'
import { toast } from '../lib/toast'

/**
 * Standard chrome wrapper for every route. Header sticky on top, main
 * scrolls underneath, footer at the bottom. Footer carries the app
 * store badges (Apple + Google Play) — placeholders until the apps
 * are published, at which point the href values get the real store
 * URLs.
 */
export function AppLayout({ children, noFooter = false }: { children: ReactNode; noFooter?: boolean }) {
  return (
    <div className="min-h-dvh flex flex-col relative z-[1]">
      <div className="app-atmosphere" aria-hidden />
      <SiteHeader />
      <VerifyingBanner />
      <main className="flex-1">{children}</main>
      {!noFooter && (
      <footer className="border-t border-border/60 bg-paper">
        <div className="mx-auto max-w-6xl px-6 py-10 grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          <div className="text-center md:text-left">
            <p className="font-semibold text-ink text-sm">DiaEx</p>
            <p className="mt-0.5 text-[11px] text-soft">an Aska product</p>
            <p className="mt-1 text-xs text-soft">Mumbai &middot; New York</p>
            <a href="mailto:info@thediaex.com" className="mt-2 inline-block text-xs text-primary hover:underline">
              info@thediaex.com
            </a>
            <p className="mt-2 space-x-3 text-[11px] text-soft">
              <a href="/terms" className="hover:text-ink hover:underline">Terms</a>
              <a href="/privacy" className="hover:text-ink hover:underline">Privacy</a>
              <a href="/refunds" className="hover:text-ink hover:underline">Refunds</a>
            </p>
          </div>
          <div className="flex items-center justify-center gap-2">
            <AppBadge platform="ios" />
            <AppBadge platform="android" />
          </div>
          <div className="text-center md:text-right text-[11px] text-soft">
            <p>20 years sourcing lab-grown diamonds</p>
            <p className="mt-1">IGI &middot; GIA certified inventory</p>
          </div>
        </div>
      </footer>
      )}
    </div>
  )
}

export function AppBadge({ platform }: { platform: 'ios' | 'android' }) {
  // Apps aren't published yet — buttons show "Coming soon" tooltip
  // and don't navigate. Replace href when App Store / Play Store URLs
  // are live.
  const label = platform === 'ios' ? 'Download on the App Store' : 'Get it on Google Play'
  return (
    <button
      type="button"
      onClick={() => toast("Apps launching soon — we'll email you a link when they go live.", 'info')}
      title={`${label} (coming soon)`}
      className="group relative inline-flex items-center gap-2 rounded-xl border border-border bg-navy text-white px-4 py-2.5 hover:bg-[#0a1a30] transition-colors"
      data-testid={`app-badge-${platform}`}
    >
      {platform === 'ios' ? (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.05 12.04c-.02-2.65 2.16-3.93 2.26-4-1.24-1.81-3.16-2.06-3.83-2.08-1.62-.17-3.18.97-4.01.97-.85 0-2.12-.95-3.49-.92-1.79.03-3.45 1.04-4.37 2.65-1.87 3.24-.48 8.04 1.34 10.66.89 1.28 1.95 2.72 3.34 2.67 1.34-.05 1.85-.86 3.47-.86 1.62 0 2.07.86 3.49.83 1.44-.02 2.36-1.3 3.24-2.59 1.03-1.49 1.45-2.93 1.47-3.01-.03-.01-2.83-1.08-2.86-4.32M14.55 4.5c.74-.89 1.24-2.13 1.1-3.36-1.06.04-2.35.7-3.11 1.6-.69.79-1.29 2.05-1.13 3.27 1.18.09 2.39-.6 3.14-1.51"/>
        </svg>
      ) : (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
          <path d="M3.609 1.814L13.792 12 3.609 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .61-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.799-4.51c.667.466.667 1.45 0 1.916l-2.486 1.34L13.13 12l2.682-2.464 2.486 1.34zM5.864 1.481l10.937 6.333-2.302 2.302L5.864 1.481z"/>
        </svg>
      )}
      <span className="flex flex-col items-start">
        <span className="text-[9px] uppercase tracking-wider opacity-80">{platform === 'ios' ? 'Download on the' : 'Get it on'}</span>
        <span className="text-sm font-semibold leading-tight">{platform === 'ios' ? 'App Store' : 'Google Play'}</span>
      </span>
      <span className="absolute -top-1 -right-1 inline-flex items-center rounded-full bg-warn text-[8px] font-bold uppercase tracking-wider text-white px-1.5 py-px">
        Soon
      </span>
    </button>
  )
}
